"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.depletionEventId = exports.supplyItemKey = exports.MODIFIER_PLACEMENT_FACTORS = exports.MODIFIER_LEVEL_FACTORS = void 0;
exports.toBaseQuantity = toBaseQuantity;
exports.fromBaseQuantity = fromBaseQuantity;
exports.formatKitchenQuantity = formatKitchenQuantity;
exports.resolveOptionQtyBase = resolveOptionQtyBase;
exports.resolveOptionPrice = resolveOptionPrice;
exports.recipeBaseLines = recipeBaseLines;
exports.expandSaleLineDepletion = expandSaleLineDepletion;
exports.matchDeals = matchDeals;
const round = (n, dp) => {
    const f = 10 ** dp;
    return Math.round((n + Number.EPSILON) * f) / f;
};
/** Stock quantity multipliers for none / light / regular / extra. */
exports.MODIFIER_LEVEL_FACTORS = {
    none: 0,
    light: 0.5,
    regular: 1,
    extra: 1.5,
};
/** Stock quantity multipliers for whole / half pizza placement. */
exports.MODIFIER_PLACEMENT_FACTORS = {
    whole: 1,
    left: 0.5,
    right: 0.5,
};
/** Kitchen item id used for a supply variation (e.g. 16 oz cup). */
const supplyItemKey = (supplyId, supplySizeId) => supplySizeId ? `${supplyId}#${supplySizeId}` : supplyId;
exports.supplyItemKey = supplyItemKey;
// ---------------------------------------------------------------------------
// Unit ladder
// ---------------------------------------------------------------------------
function packFactor(ladder, packId) {
    if (!packId || packId === ladder.baseUnit)
        return 1;
    const pack = ladder.packs.find((p) => p.id === packId);
    if (!pack)
        throw new Error(`Unknown pack "${packId}" for base unit ${ladder.baseUnit}`);
    return pack.factor;
}
/** 3 × "case" (4 gal) → 45424.8 ml. */
function toBaseQuantity(quantity, ladder, packId) {
    return round(quantity * packFactor(ladder, packId), 6);
}
/** 45424.8 ml → 3 "case". */
function fromBaseQuantity(baseQuantity, ladder, packId) {
    return round(baseQuantity / packFactor(ladder, packId), 4);
}
/** "5.5 gal" — uses displayPackId when no pack is given. */
function formatKitchenQuantity(baseQuantity, ladder, packId) {
    const id = packId ?? ladder.displayPackId;
    const pack = id ? ladder.packs.find((p) => p.id === id) : undefined;
    const value = fromBaseQuantity(baseQuantity, ladder, pack?.id);
    const pretty = Number.isInteger(value) ? String(value) : String(round(value, 2));
    return `${pretty} ${pack ? pack.name : ladder.baseUnit}`;
}
// ---------------------------------------------------------------------------
// Modifier options
// ---------------------------------------------------------------------------
/** Base units ONE chosen option uses for this size (before count/placement/level). */
function resolveOptionQtyBase(option, sizeId) {
    if (sizeId && option.qtyBySize && option.qtyBySize[sizeId] !== undefined)
        return option.qtyBySize[sizeId];
    return option.depletionQty ?? 0;
}
/**
 * Price ONE chosen option adds to one unit of the line.
 *  - add/swap: price × count × (half placement → ½ when halfPriceRule is 'half')
 *  - base (sauce/cheese level): only 'extra' is charged, once
 *  - remove/note: free
 */
function resolveOptionPrice(option, sizeId, args = {}) {
    const base = sizeId && option.priceBySize && option.priceBySize[sizeId] !== undefined
        ? option.priceBySize[sizeId]
        : option.extraPrice ?? 0;
    const behavior = option.behavior ?? 'add';
    if (behavior === 'remove' || behavior === 'note')
        return 0;
    if (behavior === 'base')
        return args.level === 'extra' ? round(base, 2) : 0;
    const count = args.count ?? 1;
    const placement = args.placement ?? 'whole';
    const half = placement !== 'whole' && (args.halfPriceRule ?? 'half') === 'half';
    return round(base * count * (half ? 0.5 : 1), 2);
}
/** Kitchen items one unit of this recipe size uses, as written. */
function recipeBaseLines(recipe, sizeId) {
    const out = new Map();
    const add = (id, q) => {
        if (!id || !(q > 0))
            return;
        out.set(id, (out.get(id) ?? 0) + q);
    };
    for (const step of recipe.steps ?? []) {
        for (const ing of step.ingredients ?? [])
            add(ing.inventoryItemId, ing.quantityPerSize?.[sizeId] ?? 0);
        for (const sup of (step.supplies ?? [])) {
            const per = sup.perMealSize?.[sizeId];
            if (per && per.supplyId) {
                add((0, exports.supplyItemKey)(per.supplyId, per.supplySizeId), per.quantity ?? 1);
            }
            else if (sup.supplyId) {
                const q = sup.quantityPerSize?.[sizeId] ?? sup.quantity ?? 0;
                add((0, exports.supplyItemKey)(sup.supplyId, sup.sizeId), q);
            }
        }
    }
    return out;
}
/**
 * recipe × size × modifiers × quantity → kitchen items used.
 *
 *  - note:   no stock
 *  - remove: drops the target recipe line (replacesIngredientId ?? ingredientId)
 *  - swap:   target line → option's item; amount = option qtyBase ?? the line's amount, × level
 *  - base:   scales the target line by level (none/light/regular/extra)
 *  - add:    option qtyBase × count × placement × level
 *
 * Result is sorted by itemId, zero lines dropped, rounded to 6 dp.
 */
function expandSaleLineDepletion({ recipe, sizeId, quantity, modifiers = [] }) {
    const lines = recipeBaseLines(recipe, sizeId);
    const bump = (id, q) => {
        if (!id || !(q > 0))
            return;
        lines.set(id, (lines.get(id) ?? 0) + q);
    };
    for (const m of modifiers) {
        const behavior = m.behavior ?? 'add';
        const level = exports.MODIFIER_LEVEL_FACTORS[m.level ?? 'regular'];
        const target = m.replacesIngredientId ?? (behavior === 'remove' || behavior === 'base' ? m.ingredientId : undefined);
        switch (behavior) {
            case 'note':
                break;
            case 'remove':
                if (target)
                    lines.delete(target);
                break;
            case 'swap': {
                const original = target ? lines.get(target) ?? 0 : 0;
                if (target)
                    lines.delete(target);
                const amount = m.qtyBase !== undefined ? m.qtyBase : original;
                bump(m.ingredientId, amount * level);
                break;
            }
            case 'base': {
                if (target && lines.has(target)) {
                    const scaled = (lines.get(target) ?? 0) * level;
                    if (scaled > 0)
                        lines.set(target, scaled);
                    else
                        lines.delete(target);
                }
                break;
            }
            case 'add':
            default:
                bump(m.ingredientId, (m.qtyBase ?? 0) * (m.count ?? 1) * exports.MODIFIER_PLACEMENT_FACTORS[m.placement ?? 'whole'] * level);
                break;
        }
    }
    return [...lines.entries()]
        .map(([itemId, q]) => ({ itemId, quantity: round(q * quantity, 6) }))
        .filter((l) => l.quantity > 0)
        .sort((a, b) => a.itemId.localeCompare(b.itemId));
}
/** Stable event id for one depletion movement — same sale line never depletes twice. */
const depletionEventId = (saleId, lineId, itemId, kind = 'use') => `${kind === 'use' ? 'ru' : 'rr'}#${saleId}#${lineId}#${itemId}`;
exports.depletionEventId = depletionEventId;
function slotAccepts(slot, line) {
    const byProduct = slot.productIds.length === 0 || slot.productIds.includes(line.productId);
    const byCategory = slot.categoryIds.length === 0 || (!!line.categoryId && slot.categoryIds.includes(line.categoryId));
    const byVariant = slot.variantIds.length === 0 || slot.variantIds.includes(line.variantId);
    const anyRule = slot.productIds.length + slot.categoryIds.length > 0;
    return anyRule && (slot.productIds.length ? byProduct : byCategory) && byVariant;
}
/**
 * Greedy deal matching: repeatedly applies the deal with the biggest saving while
 * every slot can be filled from units not yet used. Within a slot the most
 * expensive eligible unit is taken first (best value for the customer).
 * Modifier value above the slot's included allowance is added to the deal price.
 */
function matchDeals(lines, deals) {
    const remaining = new Map(lines.map((l) => [l.lineId, l.quantity]));
    const active = deals.filter((d) => d.status === 'active');
    const matches = [];
    const tryDeal = (deal) => {
        const taken = new Map();
        let regular = 0;
        let extras = 0;
        for (const slot of deal.slots) {
            for (let u = 0; u < slot.quantity; u++) {
                const pick = lines
                    .filter((l) => slotAccepts(slot, l) && (remaining.get(l.lineId) ?? 0) - (taken.get(l.lineId) ?? 0) > 0)
                    .sort((a, b) => (b.unitPrice + (b.modifierValue ?? 0)) - (a.unitPrice + (a.modifierValue ?? 0)))[0];
                if (!pick)
                    return null;
                taken.set(pick.lineId, (taken.get(pick.lineId) ?? 0) + 1);
                regular += pick.unitPrice + (pick.modifierValue ?? 0);
                extras += Math.max(0, (pick.modifierValue ?? 0) - slot.includedModifierValue);
            }
        }
        const dealTotal = round(deal.price + extras, 2);
        const discount = round(regular - dealTotal, 2);
        if (discount <= 0)
            return null;
        return { dealId: deal.dealId, dealName: deal.name, consumed: Object.fromEntries(taken), regularTotal: round(regular, 2), dealTotal, discount };
    };
    for (;;) {
        const best = active.map(tryDeal).filter((m) => !!m).sort((a, b) => b.discount - a.discount)[0];
        if (!best)
            break;
        for (const [lineId, n] of Object.entries(best.consumed))
            remaining.set(lineId, (remaining.get(lineId) ?? 0) - n);
        matches.push(best);
    }
    return matches;
}
//# sourceMappingURL=kitchen.js.map